<template>
  <div class="min-h-screen">
    <!-- Navbar -->
    <nav class="bg-white shadow-sm">
      <div class="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
        <a href="/" class="text-xl font-bold text-blue-700">{{ t('site_name') }}</a>
        <div class="flex items-center gap-4">
          <a href="/" class="text-sm text-gray-600 hover:text-blue-700">{{ t('nav.home') }}</a>
          <span class="text-sm text-gray-500">{{ userEmail }}</span>
          <a href="/users/sign_out" data-turbo-method="delete" class="text-sm text-red-600 hover:text-red-800">{{ t('auth.sign_out') }}</a>
        </div>
      </div>
    </nav>

    <!-- Admin Users -->
    <div class="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div class="flex items-center justify-between mb-8">
        <h1 class="text-2xl font-bold text-gray-900">{{ t('admin.users.title') }}</h1>
        <div class="flex items-center gap-4">
          <span class="text-sm text-gray-500">{{ t('admin.users.total', { count: userList.length }) }}</span>
          <button @click="openCreateModal" class="bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-blue-800 transition">
            {{ t('admin.users.add_user') }}
          </button>
        </div>
      </div>

      <!-- Flash message -->
      <div v-if="flash" :class="['mb-4 px-4 py-3 rounded-lg text-sm', flash.type === 'success' ? 'bg-blue-100 text-blue-800' : 'bg-red-100 text-red-800']">
        {{ flash.message }}
      </div>

      <!-- Search -->
      <div class="mb-6">
        <input
          v-model="search"
          type="text"
          :placeholder="t('admin.users.search')"
          class="w-full max-w-md px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none text-sm"
        />
      </div>

      <!-- Table -->
      <div class="bg-white shadow rounded-lg overflow-hidden">
        <table class="min-w-full divide-y divide-gray-200">
          <thead class="bg-gray-50">
            <tr>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">ID</th>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{{ t('admin.users.email') }}</th>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{{ t('admin.users.role') }}</th>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{{ t('admin.users.created_at') }}</th>
              <th class="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">{{ t('admin.users.actions') }}</th>
            </tr>
          </thead>
          <tbody class="bg-white divide-y divide-gray-200">
            <tr v-for="user in filteredUsers" :key="user.id" class="hover:bg-gray-50 transition">
              <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{{ user.id }}</td>
              <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{{ user.email }}</td>
              <td class="px-6 py-4 whitespace-nowrap text-sm">
                <span v-if="user.admin"
                      class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                  Admin
                </span>
                <span v-else
                      class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                  User
                </span>
              </td>
              <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{{ formatDate(user.created_at) }}</td>
              <td class="px-6 py-4 whitespace-nowrap text-right text-sm space-x-2">
                <button @click="openEditModal(user)" class="text-blue-600 hover:text-blue-800 font-medium">
                  {{ t('admin.users.edit') }}
                </button>
                <button @click="confirmDelete(user)" class="text-red-600 hover:text-red-800 font-medium">
                  {{ t('admin.users.delete') }}
                </button>
              </td>
            </tr>
            <tr v-if="filteredUsers.length === 0">
              <td colspan="5" class="px-6 py-8 text-center text-sm text-gray-500">
                {{ t('admin.users.no_results') }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="mt-6">
        <a href="/" class="text-blue-700 hover:text-blue-900 text-sm font-medium">
          {{ t('admin.users.back_to_home') }}
        </a>
      </div>
    </div>

    <!-- Create/Edit Modal -->
    <div v-if="showModal" class="fixed inset-0 bg-black/50 flex items-center justify-center z-50" @click.self="closeModal">
      <div class="bg-white rounded-xl shadow-xl w-full max-w-md mx-4 p-6">
        <h2 class="text-lg font-bold text-gray-900 mb-6">
          {{ editing ? t('admin.users.edit_user') : t('admin.users.new_user') }}
        </h2>

        <div v-if="formErrors.length" class="mb-4 bg-red-50 border border-red-200 rounded-lg px-4 py-3">
          <p v-for="error in formErrors" :key="error" class="text-sm text-red-700">{{ error }}</p>
        </div>

        <form @submit.prevent="submitForm" class="space-y-4">
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">{{ t('admin.users.email') }}</label>
            <input v-model="form.email" type="email" required
                   class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none text-sm" />
          </div>

          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">
              {{ t('auth.password') }}
              <span v-if="editing" class="text-gray-400 font-normal">{{ t('admin.users.password_hint') }}</span>
            </label>
            <input v-model="form.password" type="password" :required="!editing"
                   class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none text-sm" />
          </div>

          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">{{ t('auth.password_confirmation') }}</label>
            <input v-model="form.password_confirmation" type="password" :required="!editing"
                   class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none text-sm" />
          </div>

          <div class="flex items-center gap-2">
            <input v-model="form.admin" type="checkbox" id="admin-checkbox"
                   class="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500" />
            <label for="admin-checkbox" class="text-sm font-medium text-gray-700">Admin</label>
          </div>

          <div class="flex justify-end gap-3 pt-4">
            <button type="button" @click="closeModal"
                    class="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition">
              {{ t('admin.users.cancel') }}
            </button>
            <button type="submit" :disabled="submitting"
                    class="px-4 py-2 text-sm font-medium text-white bg-blue-700 rounded-lg hover:bg-blue-800 transition disabled:opacity-50">
              {{ submitting ? '...' : (editing ? t('admin.users.save') : t('admin.users.create')) }}
            </button>
          </div>
        </form>
      </div>
    </div>

    <!-- Delete Confirmation Modal -->
    <div v-if="showDeleteModal" class="fixed inset-0 bg-black/50 flex items-center justify-center z-50" @click.self="closeDeleteModal">
      <div class="bg-white rounded-xl shadow-xl w-full max-w-sm mx-4 p-6">
        <h2 class="text-lg font-bold text-gray-900 mb-2">{{ t('admin.users.confirm_delete') }}</h2>
        <p class="text-sm text-gray-600 mb-6">
          {{ t('admin.users.confirm_delete_message', { email: deleteTarget?.email }) }}
        </p>

        <div v-if="deleteError" class="mb-4 bg-red-50 border border-red-200 rounded-lg px-4 py-3">
          <p class="text-sm text-red-700">{{ deleteError }}</p>
        </div>

        <div class="flex justify-end gap-3">
          <button @click="closeDeleteModal"
                  class="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition">
            {{ t('admin.users.cancel') }}
          </button>
          <button @click="deleteUser" :disabled="submitting"
                  class="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 transition disabled:opacity-50">
            {{ submitting ? '...' : t('admin.users.delete') }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, computed } from 'vue'
import { useI18n } from 'vue-i18n'

const props = defineProps({
  userEmail: String,
  users: Array,
  csrfToken: String
})

const { t } = useI18n()

const userList = ref([...props.users])
const search = ref('')
const showModal = ref(false)
const showDeleteModal = ref(false)
const editing = ref(false)
const editingId = ref(null)
const submitting = ref(false)
const formErrors = ref([])
const deleteTarget = ref(null)
const deleteError = ref('')
const flash = ref(null)

const form = reactive({
  email: '',
  password: '',
  password_confirmation: '',
  admin: false
})

const filteredUsers = computed(() => {
  if (!search.value) return userList.value
  const q = search.value.toLowerCase()
  return userList.value.filter(u => u.email.toLowerCase().includes(q))
})

function formatDate(dateStr) {
  const date = new Date(dateStr)
  return date.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })
}

function showFlash(message, type = 'success') {
  flash.value = { message, type }
  setTimeout(() => { flash.value = null }, 4000)
}

function resetForm() {
  form.email = ''
  form.password = ''
  form.password_confirmation = ''
  form.admin = false
  formErrors.value = []
}

function openCreateModal() {
  editing.value = false
  editingId.value = null
  resetForm()
  showModal.value = true
}

function openEditModal(user) {
  editing.value = true
  editingId.value = user.id
  form.email = user.email
  form.password = ''
  form.password_confirmation = ''
  form.admin = user.admin
  formErrors.value = []
  showModal.value = true
}

function closeModal() {
  showModal.value = false
}

function confirmDelete(user) {
  deleteTarget.value = user
  deleteError.value = ''
  showDeleteModal.value = true
}

function closeDeleteModal() {
  showDeleteModal.value = false
  deleteTarget.value = null
}

async function request(url, method, body = null) {
  const opts = {
    method,
    headers: {
      'Content-Type': 'application/json',
      'X-CSRF-Token': props.csrfToken,
      'Accept': 'application/json'
    }
  }
  if (body) opts.body = JSON.stringify(body)
  return fetch(url, opts)
}

async function submitForm() {
  submitting.value = true
  formErrors.value = []

  const payload = { user: { email: form.email, admin: form.admin } }
  if (form.password) {
    payload.user.password = form.password
    payload.user.password_confirmation = form.password_confirmation
  }

  try {
    let res
    if (editing.value) {
      res = await request(`/admin/users/${editingId.value}`, 'PATCH', payload)
    } else {
      res = await request('/admin/users', 'POST', payload)
    }

    if (res.ok) {
      const data = await res.json()
      if (editing.value) {
        const idx = userList.value.findIndex(u => u.id === editingId.value)
        if (idx !== -1) userList.value[idx] = data
        showFlash(t('admin.users.updated'))
      } else {
        userList.value.unshift(data)
        showFlash(t('admin.users.created_success'))
      }
      closeModal()
    } else {
      const data = await res.json()
      formErrors.value = data.errors || [t('admin.users.error')]
    }
  } catch {
    formErrors.value = [t('admin.users.error')]
  } finally {
    submitting.value = false
  }
}

async function deleteUser() {
  submitting.value = true
  deleteError.value = ''

  try {
    const res = await request(`/admin/users/${deleteTarget.value.id}`, 'DELETE')
    if (res.ok) {
      userList.value = userList.value.filter(u => u.id !== deleteTarget.value.id)
      showFlash(t('admin.users.deleted'))
      closeDeleteModal()
    } else {
      const data = await res.json()
      deleteError.value = (data.errors || [t('admin.users.error')]).join(', ')
    }
  } catch {
    deleteError.value = t('admin.users.error')
  } finally {
    submitting.value = false
  }
}
</script>
